import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, CheckCircle2 } from "lucide-react";
import { TRUST } from "../lib/content";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { createContact } from "../lib/api";

export default function Contact() {
  useEffect(() => { document.title = "Contact — Hazrat Sufi Sarkar"; }, []);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.message) {
      toast.error("Please fill all fields.");
      return;
    }
    setLoading(true);
    try {
      await createContact(form);
      setSubmitted(true);
      toast.success("Thanks for submitting! We'll be in touch.");
    } catch {
      toast.error("Could not submit. Please try again or call us.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="pattern-emerald text-ivory pt-32 pb-24" data-testid="contact-hero">
        <div className="container-page text-center">
          <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Contact</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">Reach the Trust</h1>
          <p className="text-ivory/80 mt-5 max-w-2xl mx-auto">A letter, a call, a visit — every door of the dargah is open.</p>
        </div>
      </section>

      <section className="section-pad" data-testid="contact-grid">
        <div className="container-page grid md:grid-cols-12 gap-12">
          <aside className="md:col-span-5 space-y-8 reveal">
            <div className="card-soft p-8">
              <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Address</span>
              <h3 className="font-serif text-2xl text-emerald mt-3 flex items-start gap-3"><MapPin className="text-gold mt-1" size={20} /><span>Visit Us</span></h3>
              <p className="text-charcoal-muted mt-3 leading-relaxed">{TRUST.address}</p>
            </div>
            <div className="card-soft p-8">
              <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Phone</span>
              <h3 className="font-serif text-2xl text-emerald mt-3 flex items-start gap-3"><Phone className="text-gold mt-1" size={20} /><span>Speak with Us</span></h3>
              <ul className="text-charcoal-muted mt-3 space-y-1">
                {TRUST.phones.map(p => <li key={p}><a href={`tel:${p.replace(/\s/g, "")}`} className="hover:text-emerald">{p}</a></li>)}
              </ul>
            </div>
            <div className="card-soft p-8">
              <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Email</span>
              <h3 className="font-serif text-2xl text-emerald mt-3 flex items-start gap-3"><Mail className="text-gold mt-1" size={20} /><span>Write to Us</span></h3>
              <a href={`mailto:${TRUST.email}`} className="text-charcoal-muted mt-3 hover:text-emerald block break-all">{TRUST.email}</a>
            </div>
          </aside>

          <div className="md:col-span-7 reveal">
            {submitted ? (
              <div data-testid="contact-success" className="card-soft p-10 text-center min-h-[400px] flex flex-col items-center justify-center">
                <CheckCircle2 className="text-gold" size={56} />
                <h2 className="font-serif text-3xl text-emerald mt-5">Thanks for submitting!</h2>
                <p className="text-charcoal-muted mt-3">We'll respond at the earliest, Insha'Allah.</p>
                <p className="calligraphy text-gold text-xl mt-6">Jazak Allahu Khairan</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="card-soft p-8 md:p-10 space-y-5">
                <h2 className="font-serif text-3xl text-emerald">Send a message</h2>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="first_name" className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">First Name</Label>
                    <Input id="first_name" data-testid="contact-first" value={form.first_name} onChange={onChange("first_name")} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Last Name</Label>
                    <Input id="last_name" data-testid="contact-last" value={form.last_name} onChange={onChange("last_name")} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="contact_email" className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Email</Label>
                  <Input id="contact_email" type="email" data-testid="contact-email" value={form.email} onChange={onChange("email")} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" />
                </div>
                <div>
                  <Label htmlFor="contact_message" className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Message</Label>
                  <Textarea id="contact_message" rows={6} data-testid="contact-message" value={form.message} onChange={onChange("message")} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" />
                </div>
                <button type="submit" data-testid="contact-submit" disabled={loading} className="btn-gold disabled:opacity-60">
                  {loading ? "Sending..." : "Submit Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="section-pad pt-0" data-testid="contact-map">
        <div className="container-page">
          <div className="rounded-lg overflow-hidden border border-gold/30 shadow-xl">
            <iframe
              title="Hazrat Sufi Sarkar Trust location"
              src="https://www.google.com/maps?q=Venkatesh+Park,+Balaji+Nagar,+Lingali+Road,+Daund&output=embed"
              width="100%"
              height="420"
              style={{ border: 0, filter: "saturate(0.85) sepia(0.05)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
